import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ListScaffold } from "@/components/layout/ListScaffold";
import {
  Badge,
  type BadgeTone,
  Button,
  PageHeader,
  Select,
  Table,
  TableWrap,
  TD,
  TH,
  THead,
  TR,
} from "@/components/ui";
import { useApi } from "@/lib/api";
import { ago, personLabel } from "@/lib/format";

type Status = "new" | "triaged" | "closed" | "all";

const CATEGORY_TONE: Record<string, BadgeTone> = {
  bug: "danger",
  idea: "accent",
  other: "neutral",
};

/**
 * The inbox.
 *
 * Two buttons and no delete. `user_feedback.deleted_at` belongs to the
 * person who wrote it and to the erasure job — a triage tool that can make
 * an inconvenient report disappear is a triage tool whose numbers nobody
 * should trust, including us.
 *
 * The email is shown because replying is the whole point: a message you
 * cannot answer is a message you can only close.
 */
export function FeedbackScreen() {
  const api = useApi();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<Status>("new");

  const feedback = useQuery({
    queryKey: ["admin", "feedback", status],
    queryFn: async () => {
      const { data, error } = await api.GET("/admin/feedback", {
        params: { query: { status, limit: 50 } },
      });
      if (error) throw error;
      return data;
    },
  });

  const setStatusFor = useMutation({
    mutationFn: async (args: {
      feedbackId: string;
      status: "new" | "triaged" | "closed";
    }) => {
      const { error } = await api.PATCH("/admin/feedback/{feedbackId}", {
        params: { path: { feedbackId: args.feedbackId } },
        body: { status: args.status },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "feedback"] });
      void queryClient.invalidateQueries({
        queryKey: ["admin", "reports", "counts"],
      });
    },
  });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        eyebrow="Queues"
        title="Feedback"
        description="Straight from the app. Triaged means somebody has read it and it is going somewhere; closed means it is done or it is not happening."
      />
      <ListScaffold
        filters={
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as Status)}
          >
            <option value="new">New</option>
            <option value="triaged">Triaged</option>
            <option value="closed">Closed</option>
            <option value="all">All</option>
          </Select>
        }
        isLoading={feedback.isLoading}
        isError={feedback.isError}
        onRetry={() => void feedback.refetch()}
        isEmpty={(feedback.data?.items.length ?? 0) === 0}
        emptyTitle={status === "new" ? "Inbox is clear" : "Nothing here"}
        emptyHint={
          status === "new"
            ? "Messages arrive from the feedback sheet inside the app."
            : undefined
        }
      >
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>Message</TH>
                <TH>Kind</TH>
                <TH>From</TH>
                <TH>Device</TH>
                <TH>Sent</TH>
                <TH align="right" />
              </TR>
            </THead>
            <tbody>
              {feedback.data?.items.map((item) => (
                <TR key={item.id}>
                  <TD className="max-w-[420px]">
                    <span className="block whitespace-pre-wrap text-ink-2">
                      {item.message}
                    </span>
                    {item.imageCount > 0 ? (
                      <span className="text-micro text-muted">
                        {item.imageCount} screenshot
                        {item.imageCount === 1 ? "" : "s"} attached
                      </span>
                    ) : null}
                  </TD>
                  <TD>
                    <Badge tone={CATEGORY_TONE[item.category] ?? "neutral"}>
                      {item.category}
                    </Badge>
                  </TD>
                  <TD>
                    <span className="block">{personLabel(item.user)}</span>
                    {item.email ? (
                      <a
                        href={`mailto:${item.email}`}
                        className="font-mono text-micro text-accent hover:underline"
                      >
                        {item.email}
                      </a>
                    ) : null}
                  </TD>
                  <TD muted mono className="whitespace-nowrap">
                    {item.deviceModel ?? "—"}
                    <span className="block text-muted-2">
                      {item.os ?? "?"} {item.osVersion ?? ""} · v
                      {item.appVersion ?? "?"}
                    </span>
                  </TD>
                  <TD muted>{ago(item.createdAt)}</TD>
                  <TD align="right">
                    <span className="flex justify-end gap-1.5">
                      {item.status !== "triaged" ? (
                        <Button
                          disabled={setStatusFor.isPending}
                          onClick={() =>
                            setStatusFor.mutate({
                              feedbackId: item.id,
                              status: "triaged",
                            })
                          }
                        >
                          Triage
                        </Button>
                      ) : null}
                      {item.status !== "closed" ? (
                        <Button
                          disabled={setStatusFor.isPending}
                          onClick={() =>
                            setStatusFor.mutate({
                              feedbackId: item.id,
                              status: "closed",
                            })
                          }
                        >
                          Close
                        </Button>
                      ) : (
                        <Badge tone="neutral">closed</Badge>
                      )}
                    </span>
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </ListScaffold>
    </div>
  );
}
